// src/pages/UsersPage.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "../api/axios";
import "./UserPage.css";

import ModalShell from "../components/ui/ModalShell";
import UserProfileModal from "../components/users/UserProfileModal";

import {
  getUserReviews,
  upsertUserReview,
  deleteMyReviewForUser,
  setReviewVisibility,
} from "../api/reviews";

const PAGE_SIZES = [10, 20, 30, 50];
const FORM_ID = "users-page-edit-form";

export default function UsersPage({ locale = "kz" }) {
  /* =========================
   * i18n
   * ========================= */
  const t = useMemo(
    () => ({
      title: locale === "kz" ? "Қолданушылар" : "Пользователи",
      list: locale === "kz" ? "Қолданушылар тізімі" : "Список пользователей",
      subtitle:
        locale === "kz"
          ? "Қызметкерлер мен сыртқы қолданушыларды басқару"
          : "Управление сотрудниками и внешними пользователями",

      add: locale === "kz" ? "Қосу" : "Добавить",
      edit: locale === "kz" ? "Өңдеу" : "Редактировать",
      delete: locale === "kz" ? "Жою" : "Удалить",
      save: locale === "kz" ? "Сақтау" : "Сохранить",
      cancel: locale === "kz" ? "Болдырмау" : "Отмена",

      name: locale === "kz" ? "Аты-жөні" : "ФИО",
      username: "Username",
      email: "Email",
      password: locale === "kz" ? "Құпиясөз" : "Пароль",

      role: locale === "kz" ? "Рөл" : "Роль",
      dept: locale === "kz" ? "Бөлім" : "Отдел",
      subject: locale === "kz" ? "Пән" : "Предмет",
      status: locale === "kz" ? "Статус" : "Статус",

      perPage: locale === "kz" ? "Беттегі саны" : "На странице",
      details: locale === "kz" ? "Толық" : "Детали",
      open: locale === "kz" ? "Көру" : "Открыть",

      sync:
        locale === "kz"
          ? "Сыртқы қолданушыларды синхрондау"
          : "Синхронизация внешних пользователей",

      confirmDelete:
        locale === "kz"
          ? "Осы қолданушыны жоясыз ба?"
          : "Удалить этого пользователя?",

      required:
        locale === "kz"
          ? "Барлық қажет өрістерді толтырыңыз"
          : "Заполните обязательные поля",

      expertNeedSubject:
        locale === "kz"
          ? "Эксперт үшін пән міндетті"
          : "Для эксперта предмет обязателен",

      loading: locale === "kz" ? "Жүктелуде..." : "Загрузка...",
      error: locale === "kz" ? "Қате орын алды" : "Произошла ошибка",

      syncConfirm:
        locale === "kz"
          ? "Синхрондауды бастаймыз ба?"
          : "Начать синхронизацию?",
      syncErr: locale === "kz" ? "Қате пайда болды" : "Произошла ошибка",

      deleted: locale === "kz" ? "Қолданушы жойылды" : "Пользователь удалён",
      deleteErr:
        locale === "kz" ? "Жою кезінде қате болды" : "Ошибка при удалении",

      addUser: locale === "kz" ? "Қолданушы қосу" : "Добавить пользователя",
      editUser:
        locale === "kz" ? "Қолданушыны өңдеу" : "Редактирование пользователя",

      notFoundSubjects:
        locale === "kz" ? "Пәндер табылмады" : "Предметы не найдены",

      position: locale === "kz" ? "Қызметі" : "Должность",
      address: locale === "kz" ? "Мекенжай" : "Адрес",
      documentNumber: locale === "kz" ? "Құжат №" : "№ документа",
      bank: locale === "kz" ? "Банк" : "Банк",
      phone: locale === "kz" ? "Телефон" : "Телефон",

      searchPlaceholder:
        locale === "kz"
          ? "Іздеу: Аты-жөні / Username / Email"
          : "Поиск: ФИО / Username / Email",
      allRoles: locale === "kz" ? "Барлық рөлдер" : "Все роли",
      allDepts: locale === "kz" ? "Барлық бөлімдер" : "Все отделы",
      allStatuses: locale === "kz" ? "Барлық статустар" : "Все статусы",
      reset: locale === "kz" ? "Тазарту" : "Сбросить",
      find: locale === "kz" ? "Іздеу" : "Найти",
      nothingFound: locale === "kz" ? "Ештеңе табылмады" : "Ничего не найдено",

      profileTitle: locale === "kz" ? "Қолданушы профилі" : "Профиль пользователя",
    }),
    [locale]
  );

  /* =========================
   * API paths
   * ========================= */
  const API = useMemo(
    () => ({
      USERS_INDEX: "/backoffice/users",
      USERS_STORE: "/backoffice/users",
      USERS_UPDATE: (id) => `/backoffice/users/${id}`,
      USERS_DELETE: (id) => `/backoffice/users/${id}`,
      USER_SHOW: (id) => `/backoffice/users/${id}`,

      ROLES: "/backoffice/ref/roles",
      DEPARTMENTS: "/backoffice/ref/departments",
      SUBJECTS: "/backoffice/ref/subjects",

      PROFILE: "/internal/profile",
      SYNC: "/ebmso/sync-users",
    }),
    []
  );

  /* =========================
   * helpers
   * ========================= */
  const asStr = (v) => (v === null || v === undefined ? "" : String(v));

  const normalizeList = (payload) => {
    const x = payload?.data ?? payload;
    if (Array.isArray(x)) return x;
    if (x && typeof x === "object") return Object.values(x);
    return [];
  };

  const pick = (obj, keys) =>
    keys.reduce((acc, k) => {
      if (obj[k] !== undefined) acc[k] = obj[k];
      return acc;
    }, {});

  const getLabel = (o) =>
    (o?.name ?? o?.name_kz ?? o?.name_ru ?? o?.title ?? o?.label ?? "")
      .toString()
      .trim();

  const tokenHeader = useMemo(
    () => ({
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    }),
    []
  );

  /* =========================
   * state
   * ========================= */
  const [currentUser, setCurrentUser] = useState(null);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [subjects, setSubjects] = useState([]);

  const [filters, setFilters] = useState({
    q: "",
    iin: "",
    role: "",
    department_id: "",
    status: "",
  });

  // Add/Edit modal
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const emptyForm = useMemo(
    () => ({
      name: "",
      username: "",
      password: "",
      email: "",

      role_id: "",
      department_id: "",
      subject_id: "",
      status: "белсенді",

      iin: "",
      iik: "",
      address: "",
      position: "",
      document_number: "",
      bank_name: "",
      phone: "",
    }),
    []
  );

  const [form, setForm] = useState(emptyForm);

  // Profile modal
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileUser, setProfileUser] = useState(null); // basic row user
  const [profileUserDetails, setProfileUserDetails] = useState(null); // full GET /users/:id
  const [profileInfoLoading, setProfileInfoLoading] = useState(false);
  const [profileInfoLoadedForId, setProfileInfoLoadedForId] = useState(null);

  // Reviews
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsResp, setReviewsResp] = useState(null);
  const [myReview, setMyReview] = useState(null);
  const [reviewsLoadedForId, setReviewsLoadedForId] = useState(null);

  /* =========================
   * lookups
   * ========================= */
  const roleById = useMemo(
    () => Object.fromEntries((roles || []).map((r) => [Number(r.id), r])),
    [roles]
  );

  const deptById = useMemo(
    () => Object.fromEntries((departments || []).map((d) => [Number(d.id), d])),
    [departments]
  );

  const deptLabel = useCallback(
    (d) =>
      locale === "kz"
        ? d?.name_kz || d?.name_ru || d?.name || `ID ${d?.id}`
        : d?.name_ru || d?.name_kz || d?.name || `ID ${d?.id}`,
    [locale]
  );

  /* =========================
   * permissions
   * ========================= */
  const perms = useMemo(() => currentUser?.permissions || [], [currentUser]);
  const myRoles = useMemo(() => currentUser?.roles || [], [currentUser]);

  const canBasicEdit = useMemo(() => {
    return (
      perms.includes("users.update_basic") ||
      perms.includes("users.update") ||
      perms.includes("users.manage") ||
      myRoles.includes("admin")
    );
  }, [perms, myRoles]);

  const canFullEdit = useMemo(() => {
    return (
      perms.includes("users.update") ||
      perms.includes("users.manage") ||
      myRoles.includes("admin")
    );
  }, [perms, myRoles]);

  const isEditor = canBasicEdit;

  /* =========================
   * expert role
   * ========================= */
  const isExpertRole = useMemo(() => {
    const r = roleById[Number(form.role_id)];
    const rn = (r?.name || r?.name_ru || r?.name_kz || "")
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim();

    if (!rn) return false;

    return (
      rn.includes("эксперт") ||
      rn.includes("сарапшы") ||
      rn === "internal_expert" ||
      rn === "external_expert" ||
      rn === "ішкі сарапшы" ||
      rn === "сыртқы сарапшы"
    );
  }, [form.role_id, roleById]);

  const editDisabled = useMemo(() => isEditing && !canFullEdit, [isEditing, canFullEdit]);

  /* =========================
   * backend allowed keys
   * ========================= */
  const BASIC_KEYS = useMemo(() => ["name", "status"], []);
  const FULL_KEYS = useMemo(
    () => [
      "name",
      "password",
      "role_id",
      "department_id",
      "subject_id",
      "status",
      "iin",
      "iik",
      "address",
      "position",
      "document_number",
      "bank_name",
      "phone",
    ],
    []
  );

  /* =========================
   * fetch users
   * ========================= */
  const fetchUsers = useCallback(
    (page = 1, perPage = pagination.per_page) => {
      setLoading(true);

      const params = {
        page,
        per_page: perPage,
        q: (filters.q || "").trim() || undefined,
        iin: (filters.iin || "").trim() || undefined,
        role: filters.role || undefined,
        department_id: filters.department_id || undefined,
        status: filters.status || undefined,
      };

      axios
        .get(API.USERS_INDEX, { params, ...tokenHeader })
        .then((res) => {
          const { data, current_page, last_page, per_page, total } = res.data || {};
          setUsers(Array.isArray(data) ? data : []);
          setPagination({
            current_page: current_page ?? page,
            last_page: last_page ?? 1,
            per_page: per_page ?? perPage,
            total: total ?? 0,
          });
        })
        .catch((e) => console.error("Load users error:", e))
        .finally(() => setLoading(false));
    },
    [API.USERS_INDEX, tokenHeader, filters, pagination.per_page]
  );

  useEffect(() => {
    axios.get(API.PROFILE, tokenHeader).then((res) => setCurrentUser(res.data)).catch(() => {});

    Promise.all([
      axios.get(API.ROLES, tokenHeader),
      axios.get(API.DEPARTMENTS, tokenHeader),
      axios.get(API.SUBJECTS, tokenHeader),
    ])
      .then(([rRes, dRes, sRes]) => {
        setRoles(normalizeList(rRes.data));
        setDepartments(normalizeList(dRes.data));
        setSubjects(normalizeList(sRes.data));
      })
      .catch((e) => console.error("Refs load error", e))
      .finally(() => fetchUsers(1, 10));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* =========================
   * filters
   * ========================= */
  const applyFilters = useCallback(() => fetchUsers(1, pagination.per_page), [fetchUsers, pagination.per_page]);

  const resetFilters = useCallback(() => {
    setFilters({ q: "", iin: "", role: "", department_id: "", status: "" });
    fetchUsers(1, pagination.per_page);
  }, [fetchUsers, pagination.per_page]);

  const handlePerPageChange = useCallback(
    (n) => {
      setPagination((p) => ({ ...p, per_page: n, current_page: 1 }));
      fetchUsers(1, n);
    },
    [fetchUsers]
  );

  const handlePageChange = useCallback(
    (page) => {
      if (page >= 1 && page <= pagination.last_page) fetchUsers(page, pagination.per_page);
    },
    [fetchUsers, pagination.last_page, pagination.per_page]
  );

  /* =========================
   * add/edit modal open/close
   * ========================= */
  const closeFormModal = useCallback(() => setModalOpen(false), []);

  const openAdd = useCallback(() => {
    setForm(emptyForm);
    setEditingId(null);
    setIsEditing(false);
    setModalOpen(true);
  }, [emptyForm]);

  const openEdit = useCallback(
    (u) => {
      if (!u) return;

      setForm({
        ...emptyForm,
        name: u?.name || "",
        username: u?.username || "",
        password: "",
        email: u?.email || "",

        role_id: asStr(u?.role_id ?? u?.role?.id ?? ""),
        department_id: asStr(u?.department_id ?? u?.department?.id ?? ""),
        subject_id: asStr(u?.subject_id ?? u?.subject?.id ?? ""),
        status: u?.status || "белсенді",

        iin: u?.iin || "",
        iik: u?.iik || "",
        address: u?.address || "",
        position: u?.position || "",
        document_number: u?.document_number || "",
        bank_name: u?.bank_name || "",
        phone: u?.phone || "",
      });

      setEditingId(u.id);
      setIsEditing(true);
      setModalOpen(true);
    },
    [emptyForm]
  );

  /* =========================
   * stable footer
   * ========================= */
  const formFooter = useMemo(
    () => (
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button type="button" className="gov-btn" onClick={closeFormModal}>
          {t.cancel}
        </button>

        <button
          type="submit"
          className="gov-btn gov-btn--primary"
          disabled={!canBasicEdit}
          form={FORM_ID}
        >
          💾 {t.save}
        </button>
      </div>
    ),
    [closeFormModal, t.cancel, t.save, canBasicEdit]
  );

  /* =========================
   * submit
   * ========================= */
  const handleSubmit = useCallback(
    async (e) => {
      e?.preventDefault?.();
      if (!canBasicEdit) return;

      if (!form.name || (!isEditing && (!form.username || !form.password))) {
        alert(t.required);
        return;
      }

      if (canFullEdit && isExpertRole && !form.subject_id) {
        alert(t.expertNeedSubject);
        return;
      }

      try {
        if (isEditing) {
          let payload = canFullEdit ? pick(form, FULL_KEYS) : pick(form, BASIC_KEYS);

          if (!payload.password) delete payload.password;

          const toIntOrDrop = (obj, key) => {
            const v = obj[key];
            if (v === "" || v === null || v === undefined) {
              delete obj[key];
              return;
            }
            const n = Number(v);
            if (Number.isNaN(n)) {
              delete obj[key];
              return;
            }
            obj[key] = n;
          };

          if (canFullEdit) {
            if (!isExpertRole) payload.subject_id = null;

            if (payload.role_id === "") delete payload.role_id;
            if (payload.department_id === "") delete payload.department_id;
            if (payload.subject_id === "") delete payload.subject_id;

            toIntOrDrop(payload, "role_id");
            toIntOrDrop(payload, "department_id");
            if (payload.subject_id !== null) toIntOrDrop(payload, "subject_id");
          }

          await axios.put(API.USERS_UPDATE(editingId), payload, tokenHeader);
        } else {
          const payload = { ...form };

          if (!payload.role_id) delete payload.role_id;
          if (!payload.department_id) delete payload.department_id;
          if (!payload.subject_id) delete payload.subject_id;

          await axios.post(API.USERS_STORE, payload, tokenHeader);
        }

        setModalOpen(false);
        fetchUsers(pagination.current_page, pagination.per_page);
      } catch (err) {
        console.error("Save user error:", err);
        alert(err?.response?.data?.message || t.error);
      }
    },
    [
      API,
      BASIC_KEYS,
      FULL_KEYS,
      canBasicEdit,
      canFullEdit,
      editingId,
      fetchUsers,
      form,
      isEditing,
      isExpertRole,
      pagination.current_page,
      pagination.per_page,
      t.error,
      t.expertNeedSubject,
      t.required,
      tokenHeader,
    ]
  );

  /* =========================
   * delete / sync
   * ========================= */
  const handleDeleteUser = useCallback(
    async (id) => {
      if (!id) return;
      if (!window.confirm(t.confirmDelete)) return;

      try {
        await axios.delete(API.USERS_DELETE(id), tokenHeader);
        alert(t.deleted);

        if (profileUser?.id === id) {
          setProfileOpen(false);
          setProfileUser(null);
          setProfileUserDetails(null);
          setProfileInfoLoadedForId(null);
          setReviewsResp(null);
          setMyReview(null);
          setReviewsLoadedForId(null);
        }

        const nextPage =
          users.length === 1 && pagination.current_page > 1
            ? pagination.current_page - 1
            : pagination.current_page;

        fetchUsers(nextPage, pagination.per_page);
      } catch (err) {
        console.error("Delete error:", err);
        alert(t.deleteErr);
      }
    },
    [
      API,
      fetchUsers,
      pagination.current_page,
      pagination.per_page,
      profileUser?.id,
      t.confirmDelete,
      t.deleted,
      t.deleteErr,
      tokenHeader,
      users.length,
    ]
  );

  const handleSyncUsers = useCallback(() => {
    if (!window.confirm(t.syncConfirm)) return;

    axios
      .post(API.SYNC, {}, tokenHeader)
      .then((res) => {
        const { message = "OK", created = 0, updated = 0, deactivated = 0 } = res.data || {};
        alert(`${message}\n+ ${created} жаңа\n✏️ ${updated} жаңартылған\n❌ ${deactivated} өшірілген`);
        fetchUsers(pagination.current_page, pagination.per_page);
      })
      .catch((err) => {
        console.error("Sync error:", err);
        alert(t.syncErr);
      });
  }, [API.SYNC, fetchUsers, pagination.current_page, pagination.per_page, t.syncConfirm, t.syncErr, tokenHeader]);

  /* =========================
   * profile info
   * ========================= */
  const loadProfileInfo = useCallback(
    async (userId, force = false) => {
      if (!userId) return;
      if (!force && profileInfoLoadedForId === userId && profileUserDetails) return;

      setProfileInfoLoading(true);
      try {
        const res = await axios.get(API.USER_SHOW(userId), tokenHeader);
        const payload = res?.data?.data ?? res?.data ?? null;

        setProfileUserDetails(payload);
        setProfileInfoLoadedForId(userId);
      } catch (e) {
        console.error("Load profile info error:", e);
        setProfileUserDetails(null);
      } finally {
        setProfileInfoLoading(false);
      }
    },
    [API, tokenHeader, profileInfoLoadedForId, profileUserDetails]
  );

  /* =========================
   * reviews (profile modal)
   * ========================= */
  const loadReviews = useCallback(async (userId, page = 1, force = false) => {
    if (!userId) return;
    if (!force && page === 1 && reviewsLoadedForId === userId && reviewsResp) return;

    setReviewsLoading(true);
    try {
      const res = await getUserReviews(userId, page);
      setReviewsResp(res.data);
      setMyReview(res.data?.my_review ?? null);
      if (page === 1) setReviewsLoadedForId(userId);
    } catch (e) {
      console.error("Load reviews error:", e);
      setReviewsResp(null);
      setMyReview(null);
    } finally {
      setReviewsLoading(false);
    }
  }, [reviewsLoadedForId, reviewsResp]);

  const openProfile = useCallback((u) => {
    setProfileUser(u);
    setProfileOpen(true);

    // reset lazy-loaded parts
    setProfileUserDetails(null);
    setProfileInfoLoadedForId(null);

    setReviewsResp(null);
    setMyReview(null);
    setReviewsLoadedForId(null);
  }, []);

  const closeProfile = useCallback(() => {
    setProfileOpen(false);
    setProfileUser(null);
    setProfileUserDetails(null);
    setProfileInfoLoadedForId(null);

    setReviewsResp(null);
    setMyReview(null);
    setReviewsLoadedForId(null);

    setProfileInfoLoading(false);
    setReviewsLoading(false);
  }, []);

  const handleInfoTabOpen = useCallback(() => {
    if (!profileUser?.id) return;
    loadProfileInfo(profileUser.id);
  }, [profileUser?.id, loadProfileInfo]);

  const handleReviewsTabOpen = useCallback(() => {
    if (!profileUser?.id) return;
    loadReviews(profileUser.id, 1);
  }, [profileUser?.id, loadReviews]);

  const handleReviewsPageChange = useCallback(
    (page) => {
      if (!profileUser?.id) return;
      loadReviews(profileUser.id, page, true);
    },
    [profileUser?.id, loadReviews]
  );

  const handleReviewSave = useCallback(
    async ({ rating, text }) => {
      if (!profileUser?.id) return;
      await upsertUserReview(profileUser.id, { rating, review_text: text });
      await loadReviews(profileUser.id, reviewsResp?.reviews?.current_page || 1, true);
    },
    [profileUser?.id, reviewsResp?.reviews?.current_page, loadReviews]
  );

  const handleReviewDeleteMine = useCallback(
    async () => {
      if (!profileUser?.id) return;
      await deleteMyReviewForUser(profileUser.id);
      await loadReviews(profileUser.id, reviewsResp?.reviews?.current_page || 1, true);
    },
    [profileUser?.id, reviewsResp?.reviews?.current_page, loadReviews]
  );

  const handleReviewToggleVisibility = useCallback(
    async (reviewId, visible) => {
      if (!profileUser?.id) return;
      await setReviewVisibility(reviewId, visible);
      await loadReviews(profileUser.id, reviewsResp?.reviews?.current_page || 1, true);
    },
    [profileUser?.id, reviewsResp?.reviews?.current_page, loadReviews]
  );

  /* =========================
   * render helpers
   * ========================= */
  const renderRole = useCallback((u) => {
    const rn = u?.role_name || u?.role?.name || "";
    if (!rn) return u?.role_id ? String(u.role_id) : "-";
    return rn;
  }, []);

  const renderDept = useCallback(
    (u) => {
      const dep = u?.department ?? deptById[Number(u?.department_id)];
      return dep ? deptLabel(dep) : "-";
    },
    [deptById, deptLabel]
  );

  const renderSubject = useCallback((u) => {
    if (!u?.subject) return "-";
    return getLabel(u.subject) || "-";
  }, []);

  /* =========================
   * UI
   * ========================= */
  return (
    <div className="dashboard-body">
      <div className="gov-card">
        <div className="gov-header">
          <div>
            <h2 className="gov-title">👤 {t.list}</h2>
            <p className="gov-subtitle">{t.subtitle}</p>
          </div>

          <div className="gov-actions">
            <div className="gov-field">
              <span>{t.perPage}:</span>
              <select
                className="gov-select"
                value={pagination.per_page}
                onChange={(e) => handlePerPageChange(Number(e.target.value))}
              >
                {PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>

            {isEditor && (
              <>
                <button className="gov-btn gov-btn--primary" type="button" onClick={openAdd}>
                  ➕ {t.add}
                </button>
                <button className="gov-btn gov-btn--ghost" type="button" onClick={handleSyncUsers}>
                  🔄 {t.sync}
                </button>
              </>
            )}
          </div>
        </div>

        <div className="gov-filters">
          <input
            className="gov-input"
            placeholder={t.searchPlaceholder}
            value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                applyFilters();
              }
            }}
          />

          <input
            className="gov-input"
            placeholder="IIN"
            value={filters.iin}
            onChange={(e) => setFilters((f) => ({ ...f, iin: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                applyFilters();
              }
            }}
          />

          <select
            className="gov-select"
            value={filters.role}
            onChange={(e) => setFilters((f) => ({ ...f, role: e.target.value }))}
          >
            <option value="">{t.allRoles}</option>
            {roles.map((r) => (
              <option key={r.id} value={r.name}>
                {r.name_ru || r.name_kz || r.title || r.label || r.name}
              </option>
            ))}
          </select>

          <select
            className="gov-select"
            value={filters.department_id}
            onChange={(e) => setFilters((f) => ({ ...f, department_id: e.target.value }))}
          >
            <option value="">{t.allDepts}</option>
            {(departments || []).map((d) => (
              <option key={d.id} value={asStr(d.id)}>
                {deptLabel(d)}
              </option>
            ))}
          </select>

          <select
            className="gov-select"
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          >
            <option value="">{t.allStatuses}</option>
            <option value="белсенді">{locale === "kz" ? "Белсенді" : "Активен"}</option>
            <option value="белсенді емес">{locale === "kz" ? "Белсенді емес" : "Неактивен"}</option>
          </select>

          <button className="gov-btn gov-btn--primary" type="button" onClick={applyFilters}>
            🔎 {t.find}
          </button>

          <button className="gov-btn" type="button" onClick={resetFilters}>
            🧹 {t.reset}
          </button>
        </div>

        <div className="gov-table-wrap">
          <table className="gov-table">
            <thead>
              <tr>
                <th>#</th>
                <th>{t.name}</th>
                <th>{t.username}</th>
                <th>{t.email}</th>
                <th>{t.role}</th>
                <th>{t.dept}</th>
                <th>{t.subject}</th>
                <th>{t.status}</th>
                <th>{t.details}</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="gov-muted" style={{ padding: 16 }}>
                    {t.loading}
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={9} className="gov-muted" style={{ padding: 16 }}>
                    {t.nothingFound}
                  </td>
                </tr>
              ) : (
                users.map((u, i) => {
                  const rowIndex = (pagination.current_page - 1) * (pagination.per_page || 10) + i + 1;
                  const isActive = u.status === "белсенді";

                  return (
                    <tr key={u.id}>
                      <td>{rowIndex}</td>
                      <td>{u.name}</td>
                      <td>{u.username}</td>
                      <td className="gov-muted">{u.email || "-"}</td>
                      <td>{renderRole(u)}</td>
                      <td>{renderDept(u)}</td>
                      <td>{renderSubject(u)}</td>

                      <td>
                        <span className={`gov-pill ${isActive ? "gov-pill--ok" : "gov-pill--bad"}`}>
                          <span className="gov-dot" />
                          {locale === "kz"
                            ? isActive
                              ? "Белсенді"
                              : "Белсенді емес"
                            : isActive
                            ? "Активен"
                            : "Неактивен"}
                        </span>
                      </td>

                      <td>
                        <button className="gov-btn" type="button" onClick={() => openProfile(u)}>
                          {t.open}
                        </button>

                        {canBasicEdit && (
                          <button
                            className="gov-btn gov-btn--ghost"
                            type="button"
                            onClick={() => openEdit(u)}
                            style={{ marginLeft: 8 }}
                          >
                            ✏️ {t.edit}
                          </button>
                        )}

                        {canFullEdit && (
                          <button
                            className="gov-btn gov-btn--danger"
                            type="button"
                            onClick={() => handleDeleteUser(u.id)}
                            style={{ marginLeft: 8 }}
                            title={t.delete}
                          >
                            🗑️ {t.delete}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="gov-pagination">
          <button
            className="gov-page"
            type="button"
            onClick={() => handlePageChange(pagination.current_page - 1)}
            disabled={pagination.current_page === 1}
          >
            ⬅
          </button>

          {(() => {
            const out = [];
            const total = pagination.last_page;
            const cur = pagination.current_page;

            const start = Math.max(2, cur - 2);
            const end = Math.min(total - 1, cur + 2);

            out.push(
              <button
                key={1}
                className={`gov-page ${cur === 1 ? "is-active" : ""}`}
                type="button"
                onClick={() => handlePageChange(1)}
              >
                1
              </button>
            );

            if (start > 2) out.push(<span key="sd" className="gov-muted">…</span>);

            for (let p = start; p <= end; p++) {
              out.push(
                <button
                  key={p}
                  className={`gov-page ${cur === p ? "is-active" : ""}`}
                  type="button"
                  onClick={() => handlePageChange(p)}
                >
                  {p}
                </button>
              );
            }

            if (end < total - 1) out.push(<span key="ed" className="gov-muted">…</span>);

            if (total > 1) {
              out.push(
                <button
                  key={total}
                  className={`gov-page ${cur === total ? "is-active" : ""}`}
                  type="button"
                  onClick={() => handlePageChange(total)}
                >
                  {total}
                </button>
              );
            }

            return out;
          })()}

          <button
            className="gov-page"
            type="button"
            onClick={() => handlePageChange(pagination.current_page + 1)}
            disabled={pagination.current_page === pagination.last_page}
          >
            ➡
          </button>
        </div>
      </div>

      <ModalShell open={profileOpen} onClose={closeProfile} title={t.profileTitle} size="lg">
        {profileUser ? (
          <UserProfileModal
            user={profileUser}
            userDetails={profileUserDetails}
            locale={locale}
            infoLoading={profileInfoLoading}
            reviewsLoading={reviewsLoading}
            reviewsResp={reviewsResp}
            myReview={myReview}
            canEdit={canBasicEdit}
            canDelete={canFullEdit}
            canWriteReview={true}
            canManageReview={false}
            onEdit={() => openEdit(profileUserDetails || profileUser)}
            onDelete={() => handleDeleteUser(profileUser?.id)}
            onInfoTabOpen={handleInfoTabOpen}
            onReviewsTabOpen={handleReviewsTabOpen}
            onReviewsPageChange={handleReviewsPageChange}
            onReviewSave={handleReviewSave}
            onReviewDeleteMine={handleReviewDeleteMine}
            onReviewToggleVisibility={handleReviewToggleVisibility}
          />
        ) : null}
      </ModalShell>

      <ModalShell
        open={modalOpen}
        onClose={closeFormModal}
        title={isEditing ? t.editUser : t.addUser}
        size="lg"
        footer={formFooter}
      >
        <form id={FORM_ID} onSubmit={handleSubmit}>
          <div className="gov-grid">
            <input
              className="gov-input"
              placeholder={t.name}
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              required
            />

            <input
              className="gov-input"
              placeholder="Username"
              value={form.username}
              onChange={(e) => setForm((p) => ({ ...p, username: e.target.value }))}
              required={!isEditing}
              disabled={isEditing}
            />

            <input
              className="gov-input"
              placeholder={t.password}
              type="password"
              value={form.password}
              onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
              required={!isEditing}
              disabled={editDisabled}
            />

            <input
              className="gov-input"
              placeholder="Email"
              type="email"
              value={form.email || ""}
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              disabled={isEditing}
            />

            <select
              className="gov-select"
              value={form.role_id}
              onChange={(e) => setForm((p) => ({ ...p, role_id: e.target.value }))}
              disabled={editDisabled}
            >
              <option value="">{t.role}</option>
              {roles.map((r) => (
                <option key={r.id} value={asStr(r.id)}>
                  {r.name_ru || r.name_kz || r.name || `ID ${r.id}`}
                </option>
              ))}
            </select>

            <select
              className="gov-select"
              value={form.department_id || ""}
              onChange={(e) => setForm((p) => ({ ...p, department_id: e.target.value }))}
              disabled={editDisabled}
            >
              <option value="">{t.dept}</option>
              {(departments || []).map((d) => (
                <option key={d.id} value={asStr(d.id)}>
                  {deptLabel(d)}
                </option>
              ))}
            </select>

            <select
              className="gov-select"
              value={form.subject_id || ""}
              onChange={(e) => setForm((p) => ({ ...p, subject_id: e.target.value }))}
              disabled={editDisabled || !isExpertRole}
              title={isExpertRole ? "" : locale === "kz" ? "Тек эксперттерге" : "Только для экспертов"}
            >
              <option value="">{t.subject}</option>
              {subjects.length > 0 ? (
                subjects.map((s) => (
                  <option key={s.id} value={asStr(s.id)}>
                    {getLabel(s) || `ID ${s.id}`}
                  </option>
                ))
              ) : (
                <option value="" disabled>
                  — {t.notFoundSubjects} —
                </option>
              )}
            </select>

            <select
              className="gov-select"
              value={form.status}
              onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
            >
              <option value="белсенді">{locale === "kz" ? "Белсенді" : "Активен"}</option>
              <option value="белсенді емес">{locale === "kz" ? "Белсенді емес" : "Неактивен"}</option>
            </select>

            <input
              className="gov-input"
              placeholder="IIN"
              value={form.iin}
              onChange={(e) => setForm((p) => ({ ...p, iin: e.target.value }))}
              disabled={editDisabled}
            />

            <input
              className="gov-input"
              placeholder="IIK"
              value={form.iik}
              onChange={(e) => setForm((p) => ({ ...p, iik: e.target.value }))}
              disabled={editDisabled}
            />

            <input
              className="gov-input"
              placeholder={t.position}
              value={form.position}
              onChange={(e) => setForm((p) => ({ ...p, position: e.target.value }))}
              disabled={editDisabled}
            />

            <input
              className="gov-input"
              placeholder={t.documentNumber}
              value={form.document_number}
              onChange={(e) => setForm((p) => ({ ...p, document_number: e.target.value }))}
              disabled={editDisabled}
            />

            <input
              className="gov-input"
              placeholder={locale === "kz" ? "Банк атауы" : "Банк"}
              value={form.bank_name}
              onChange={(e) => setForm((p) => ({ ...p, bank_name: e.target.value }))}
              disabled={editDisabled}
            />

            <input
              className="gov-input"
              placeholder={t.phone}
              value={form.phone}
              onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
              disabled={editDisabled}
            />

            <input
              className="gov-input"
              placeholder={t.address}
              value={form.address}
              onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
              disabled={editDisabled}
            />
          </div>
        </form>
      </ModalShell>
    </div>
  );
}